<template>
  <AuthWrapper
    :title="$t('auth.welcome')"
    :subtitle="$t('auth.subtitle')"
    :src="logoSource"
    class="[&_h1]:pt-0 mt-0"
    show-logo>
    <template #inputs>
      <form class="flex flex-col w-full gap-1">
        <div>
          <label for="dbName" class="text-sm font-medium capitalize">{{
            $t('auth.serverIP')
          }}</label>
          <div class="flex items-center justify-between">
            <BaseInput
              v-model="serverIp"
              type="text"
              color="light"
              prefix
              class="flex-1 my-1"
              :error="
                $v.serverIp.required.$invalid || $v.port.required.$invalid
                  ? $v.serverIp.required.$message
                  : ''
              ">
              <template #prefix>
                <span
                  class="inline-flex items-center px-3 font-light rounded-l-md text-slate-500 bg-slate-100">
                  <select
                    v-model="protocol"
                    class="bg-transparent border-0 outline-none appearance-none">
                    <option v-for="opt in protocolOptions" :key="opt">
                      {{ opt }}
                    </option>
                  </select>
                </span></template
              >
              <template #suffix>
                <div class="inset-y-0 flex items-center px-2 pointer-events-none">
                  <span class="font-light text-slate-400">:</span>
                </div>
                <input
                  v-model.trim="port"
                  class="w-4/12 h-full bg-transparent focus-visible:outline-none"
                  type="number" />
              </template>
            </BaseInput>
            <BaseButton
              class="ml-2"
              :disabled="!serverIp || !port"
              :class="{ 'pointer-events-none': !serverIp || !port }"
              @click="checkConnection">
              <img src="@/assets/images/cloud.svg" class="h-5" />
            </BaseButton>
          </div>
        </div>
        <div>
          <label for="databaseName" class="text-sm font-medium capitalize">{{
            $t('auth.dbName')
          }}</label>
          <BaseInput
            v-model="dbName"
            type="text"
            color="light"
            class="my-1"
            :error="$v.databaseName.required.$invalid ? $v.databaseName.required.$message : ''">
          </BaseInput>
        </div>
        <div>
          <label for="username" class="text-sm font-medium capitalize">{{
            $t('auth.username')
          }}</label>
          <BaseInput
            v-model="username"
            type="text"
            color="light"
            class="my-1"
            :error="$v.username.required.$invalid ? $v.username.required.$message : ''">
          </BaseInput>
        </div>

        <div>
          <label for="password" class="text-sm font-medium capitalize">{{
            $t('auth.password')
          }}</label>
          <BaseInput
            v-model="password"
            type="password"
            color="light"
            class="my-1"
            show-password-toggle
            :error="$v.password.required.$invalid ? $v.password.required.$message : ''">
          </BaseInput>
        </div>
        <div>
          <label for="language" class="text-sm font-medium capitalize">{{
            $t('auth.language')
          }}</label>
          <SingleSelect
            v-model="appLanguage"
            :options="languages"
            class="h-10 my-1"
            item-text="name"
            :placeholder="$t('auth.languageSelect')"
            @on-change="storeLocale" />
        </div>
        <div class="flex items-center font-light text-sm gap-x-2.5">
          <Checkbox v-model="rememberMe" /> {{ $t('auth.rememberMe') }}
          <button
            type="button"
            class="ml-auto text-theme-500 hover:text-theme-700"
            @click="onForgotPasswordClick">
            {{ $t('auth.forgotPassword') }}
          </button>
        </div>
      </form>
    </template>
    <template #button>
      <BaseButton
        size="base"
        color="primary"
        class="w-full mt-5 transition ease-in delay-150 drop-shadow-xl"
        :disabled="!username || !password || !isConnected || !serverIp || !port"
        @click="onLogin">
        {{ $t('auth.login') }}
      </BaseButton>
    </template>
    <template #bottom>
      <div class="p-2 mt-3 text-sm rounded-lg bg-slate-100">
        <div class="flex items-center font-light capitalize">
          <span
            class="w-2.5 h-2.5 mr-2 rounded-full"
            :class="status === 'online' ? 'bg-green-500' : 'bg-red-500'"></span>
          {{ $t(`auth.${status}`) }}
        </div>
      </div>
      <ResetPassword
        v-if="showResetPasswordModal"
        :database-name="dbName"
        @submitted="onPasswordReset"
        @close-modal="showResetPasswordModal = !showResetPasswordModal" />
    </template>
  </AuthWrapper>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useToastStore } from '@/stores/toast';
import {
  getCompanyMWParams,
  getSessionDetails,
  pingServer,
  userLogin,
  getUserMenus,
} from '@/services/api/auth';
import AuthWrapper from '@/components/auth/AuthWrapper.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import SingleSelect from '@/components/common/SingleSelect.vue';
import Checkbox from '@/components/common/BaseCheckbox.vue';
import ResetPassword from '@/components/auth/ResetPassword.vue';
import type { MWParam } from '@/types/MW';
import { MWParams, LocalStorageKeys } from '@/utils/constants';

const { t: $t, locale } = useI18n({ useScope: 'global' });

const languages = [
  { code: 'en', name: 'English' },
  { code: 'el', name: 'Ελληνικά' },
];

const router = useRouter();
const authStore = useAuthStore();
const toastStore = useToastStore();

const {
  isConnected,
  isSessionValid,
  username,
  password,
  status,
  serverIp,
  port,
  dbName,
  language,
  protocol,
  isAdmin,
} = storeToRefs(useAuthStore());

let intervalId: ReturnType<typeof setInterval> | undefined = undefined;

const databaseName = ref(dbName);
const rememberMe = ref(false);
const logoSource = ref<string>('');

const protocolOptions = ['https://', 'http://'];
onMounted(async () => {
  if (isSessionValid.value == false) toastStore.error({ text: $t('auth.expiredSession') });
  await checkConnection();
  const logo = await generateLogo();
  if (logo) logoSource.value = logo;

  const storageRememberMe = localStorage.getItem(LocalStorageKeys.RememberMe);
  if (storageRememberMe) {
    rememberMe.value = storageRememberMe === 'true';
  }
});

onUnmounted(() => {
  clearInterval(intervalId);
});

watch(isConnected, (newValue) => {
  status.value = newValue ? 'online' : 'offline';
});

const appLanguage = ref(
  languages.find((x: any) =>
    language.value ? x.code === language.value : x.code === locale.value
  ) || languages[0]
);

async function storeLocale() {
  locale.value = appLanguage.value.code;
  if (appLanguage.value) authStore.language = appLanguage.value.code;
}

async function onLogin() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  authStore.addRequest();
  const result = await userLogin();

  if (!result) return;

  if (rememberMe.value) {
    localStorage.setItem(LocalStorageKeys.Username, username.value);
    localStorage.setItem(LocalStorageKeys.Password, password.value);
    localStorage.setItem(LocalStorageKeys.DbName, dbName.value);
    localStorage.setItem(LocalStorageKeys.ServerIP, serverIp.value);
    localStorage.setItem(LocalStorageKeys.ServerPort, port.value);
    localStorage.setItem(LocalStorageKeys.Protocol, protocol.value);
  }
  localStorage.setItem(LocalStorageKeys.RememberMe, rememberMe.value.toString());
  localStorage.setItem(LocalStorageKeys.SessionID, result);
  localStorage.setItem(LocalStorageKeys.Language, appLanguage.value.code);
  const mwParams = await getCompanyMWParams();

  if (mwParams) localStorage.setItem(LocalStorageKeys.MwParams, JSON.stringify(mwParams));
  const logo = await generateLogo();
  if (logo) logoSource.value = logo;

  const sessionDetails = await getSessionDetails();
  localStorage.setItem(LocalStorageKeys.SessionDetails, JSON.stringify(sessionDetails));
  isAdmin.value = sessionDetails.isAdmin;

  localStorage.setItem(LocalStorageKeys.InstallationModules, JSON.stringify(await getUserMenus()));

  isSessionValid.value = true;
  authStore.removeRequest();
  router.push('/dashboard');
}

async function checkConnection() {
  isConnected.value = await pingServer();
  if (isConnected.value) toastStore.success({ text: $t('auth.connected') });
}

//validation

const rules = computed(() => ({
  username: { required: helpers.withMessage('Username is required', required) },
  password: { required: helpers.withMessage('Password is required', required) },
  databaseName: { required: helpers.withMessage('Please enter database name.', required) },
  serverIp: { required: helpers.withMessage('Please enter server IP and server port.', required) },
  port: { required },
}));

const $v = useVuelidate(
  rules,
  { username, password, databaseName, serverIp, port },
  { $lazy: true }
);

const showResetPasswordModal = ref(false);

function onForgotPasswordClick() {
  $v.value.databaseName.$touch();
  if ($v.value.$invalid) return;
  showResetPasswordModal.value = true;
}

function onPasswordReset(message: string) {
  toastStore.success({ timeout: 5000, text: `${$t('messages.success.resetPassword')} ${message}` });
  showResetPasswordModal.value = false;
}

async function generateLogo(): Promise<string | undefined> {
  const memoryMWParams = localStorage.getItem(LocalStorageKeys.MwParams);
  if (memoryMWParams) {
    const params = JSON.parse(memoryMWParams) as MWParam[];
    if (params && params.length > 0) {
      const logoParam = params.find((x) => x.code === MWParams.COMPANY_LOGO);
      if (logoParam) {
        const companyLogo = 'data:image/jpg;base64,' + logoParam.defaultValue;
        localStorage.setItem(LocalStorageKeys.CompanyLogo, companyLogo);
        return companyLogo;
      }
    }
  }
}
</script>
